import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fmtDateTime } from "@/lib/intl";
import { Card } from "@/components/ui/card";
import { followUpTone } from "@/components/enquiries/NextAction";

const TONE = { overdue: "text-red-700 font-semibold", today: "text-amber-700", upcoming: "text-zinc-500" };
const time = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** Rows for "My follow-ups today": next action · contact, and when (red "Overdue · …" or today's time). */
export const FollowUpRows = ({ items }) => {
  const navigate = useNavigate();
  return (items || []).slice(0, 6).map((f) => {
    const tone = followUpTone(f.next_follow_up_at);
    return (
      <li key={f.id} onClick={() => navigate(`/enquiries?open=${f.id}`)} data-testid={`panel-follow-up-${f.id}`}
        className="-mx-1 flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-zinc-50">
        <span className="flex-1 truncate">
          <b className="text-zinc-900">{f.next_action || "Follow up"}</b>
          <span className="text-zinc-500"> · {f.name || f.subject || "(no name)"}</span>
          {f.unassigned && <span className="ml-1 rounded border border-zinc-200 px-1 text-2xs uppercase text-zinc-500">Unassigned</span>}
        </span>
        <span className={cn("flex-shrink-0 tabular-nums", TONE[tone])} data-testid={`panel-follow-up-when-${f.id}`}>
          {tone === "overdue" ? `Overdue · ${fmtDateTime(f.next_follow_up_at)}` : time(f.next_follow_up_at)}
        </span>
      </li>
    );
  });
};

/** Stand-alone card (staff land on Appointments, not the Dashboard); hidden when nothing is due. */
export const MyFollowUpsCard = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => api.get("/enquiries/my-follow-ups").then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 120_000);
    return () => clearInterval(id);
  }, []);
  if (!data?.count) return null;
  return (
    <Card className="p-3" data-testid="panel-my-follow-ups">
      <div className="mb-2 flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded border border-rose-200 bg-rose-50 text-rose-700">
          <CalendarClock className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1 text-sm font-semibold text-zinc-950">My follow-ups today</div>
        <span className="rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-rose-700">{data.count}</span>
      </div>
      <ul className="mb-1"><FollowUpRows items={data.items} /></ul>
      <button type="button" onClick={() => navigate("/enquiries?view=follow_ups")} data-testid="panel-my-follow-ups-action"
        className="mt-1 flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
        Open Follow-ups due <ArrowRight className="h-3 w-3" />
      </button>
    </Card>
  );
};
